import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material'
import { Button } from '@ds/components'
import DescriptionIcon from '@mui/icons-material/Description'
import DownloadIcon from '@mui/icons-material/Download'
import { useCsvData } from '../hooks'

interface CsvPreviewProps {
  url: string
  variant: 'card' | 'chat'
  onError?: () => void
}

export const CsvPreview = ({ url, variant, onError }: CsvPreviewProps) => {
  const isChat = variant === 'chat'
  const {
    csvData,
    isLoading,
    error,
    displayedRows,
    loadMoreRows,
    hasMore,
    headers,
    dataRows,
    handleLoadMore,
  } = useCsvData({ url, isChat, onError })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error || csvData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          p: 2,
        }}
      >
        <DescriptionIcon sx={{ fontSize: isChat ? 40 : 64, color: 'text.secondary', mb: 1 }} />
        <Typography variant={isChat ? 'body2' : 'body1'} color="text.secondary" mb={1}>
          {error || 'CSV file is empty'}
        </Typography>
        <IconButton
          onClick={e => {
            e.stopPropagation()
            window.open(url, '_blank')
          }}
          sx={{ color: 'primary.main' }}
        >
          <DownloadIcon />
        </IconButton>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          maxHeight: isChat ? 300 : 'none',
          overflow: 'auto',
          '& .MuiTableCell-root': {
            fontSize: isChat ? '0.75rem' : '0.875rem',
            padding: isChat ? '4px 8px' : '8px 16px',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Table stickyHeader size={isChat ? 'small' : 'medium'}>
          {headers.length > 0 && (
            <TableHead>
              <TableRow>
                {headers.map((header, index) => (
                  <TableCell
                    key={`header-${header || `col-${index}`}`}
                    sx={{ fontWeight: 600, bgcolor: 'background.default' }}
                  >
                    {header || `Column ${index + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {dataRows.map((row, rowIndex) => {
              const rowKey = row.join('-') || `row-${rowIndex}`
              return (
                <TableRow key={rowKey} hover>
                  {headers.map((header, cellIndex) => (
                    <TableCell key={`${rowKey}-${header || cellIndex}`}>
                      {row[cellIndex] || ''}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {displayedRows} of {csvData.length} rows.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={e => {
                e.stopPropagation()
                handleLoadMore()
              }}
            >
              Load {Math.min(loadMoreRows, csvData.length - displayedRows)} more
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={e => {
                e.stopPropagation()
                window.open(url, '_blank')
              }}
            >
              Download
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
